import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./FinancialReport.css";
import Preloader from "../Preloader/Preloader";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import {
  getAccountingReportHours,
  getAccountingReportExcel,
  getAccountingReportExcelV3,
  getUsersSummary,
  getUsersDirectory,
  getAllGroups,
} from "../../api/api";
import { safeNumber } from "../../utils/timeUtils";
import { normalizeIsFired } from "../../utils/buildUserUpdatePayload";
import Select from "react-select";

registerLocale("ru", ru);
setDefaultLocale("ru");

const getUserOptionLabel = (user) => {
  const name = `${user.surname || ""} ${user.name || ""}`.trim() || "Без имени";
  return user.isFired ? `${name} (уволен)` : name;
};

function FinancialReport() {
  // Добавляем новую вспомогательную функцию для форматирования дат
  const getFormattedDate = (date) => {
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
    return adjustedDate.toISOString().split("T")[0];
  };

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState("department");
  const [noDataMessage, setNoDataMessage] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingV3, setIsExportingV3] = useState(false);
  const [allUsers, setAllUsers] = useState(false);
  const [exportError, setExportError] = useState("");
  const [firedUserIds, setFiredUserIds] = useState(() => new Set());
  const [showFired, setShowFired] = useState(false);

  const withFiredFlag = (userList, firedIds = firedUserIds) =>
    (userList || []).map((user) => ({
      ...user,
      isFired: firedIds.has(Number(user.id)),
    }));

  const isUserFired = (user) =>
    Boolean(user?.isFired) || firedUserIds.has(Number(user?.id));

  const getVisibleUsers = (userList) =>
    showFired
      ? userList || []
      : (userList || []).filter((user) => !isUserFired(user));

  const getReportUserIds = () => {
    const sourceUsers = allUsers ? users : selectedUsers;
    return getVisibleUsers(sourceUsers).map((user) => user.id);
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "200px",
      opacity: state.isLoading || state.isDisabled ? 0.7 : 1,
      cursor: state.isLoading || state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: state.isLoading ? "#E0E0E0" : "#999",
      },
    }),
    menu: (provided) => ({
      ...provided,
      width: "392px",
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#E8E8E8",
      borderRadius: "5px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "14px",
      fontFamily: "Manrope",
    }),
  };

  // Модифицируем useEffect для загрузки отделов
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const allGroups = await getAllGroups();
        const uniqueDepartments = allGroups
          .filter((group) => group.is_active)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }));
        setDepartments(uniqueDepartments);
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      }
    };

    loadDepartments();
  }, []); // Зависимость пустая, так как нам нужно загрузить отделы только один раз при монтировании

  // Модифицируем useEffect для загрузки пользователей
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setIsLoadingUsers(true);
        const [summaryResponse, directory] = await Promise.all([
          getUsersSummary({
            pageSize: 1000,
          }),
          getUsersDirectory().catch((error) => {
            console.error("Не удалось загрузить статусы увольнения:", error);
            return [];
          }),
        ]);

        const firedIds = new Set(
          (directory || [])
            .filter((user) => normalizeIsFired(user.is_fired))
            .map((user) => Number(user.id))
        );
        setFiredUserIds(firedIds);
        setUsers(withFiredFlag(summaryResponse.users || [], firedIds));
      } catch (error) {
        console.error("Ошибка при загрузке пользователей:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  // Модифицируем useEffect для обработки выбора отдела
  useEffect(() => {
    if (selectedDepartment && selectionMode === "department") {
      console.log("Selected department:", selectedDepartment);

      const loadDepartmentUsers = async () => {
        try {
          const response = await getUsersSummary({
            groupId: selectedDepartment.value,
            pageSize: 1000,
          });

          if (response && response.users) {
            setSelectedUsers(withFiredFlag(response.users));
          }
        } catch (error) {
          console.error("Ошибка при загрузке пользователей отдела:", error);
        }
      };

      loadDepartmentUsers();
      setReportsData([]);
    }
  }, [selectedDepartment, selectionMode, firedUserIds]);

  useEffect(() => {
    if (showFired || selectionMode !== "individual") return;
    setSelectedUsers((prev) => prev.filter((user) => !isUserFired(user)));
  }, [showFired, firedUserIds, selectionMode]);

  // Модифицируем useEffect для загрузки данных отчета
  useEffect(() => {
    const fetchReportData = async () => {
      const reportUserIds = getReportUserIds();

      if (
        (!selectedUsers.length && selectionMode === "individual" && !allUsers) ||
        (!selectedDepartment && selectionMode === "department" && !allUsers) ||
        (allUsers && !users.length) ||
        (selectionMode === "department" &&
          selectedDepartment &&
          !selectedUsers.length &&
          !allUsers) ||
        !reportUserIds.length
      ) {
        setReportsData([]);
        setNoDataMessage("");
        return;
      }

      setLoading(true);
      setNoDataMessage("");

      try {
        const dateFrom = getFormattedDate(getMonthStart(dateRange.startDate));
        const dateTo = getFormattedDate(getMonthEnd(dateRange.startDate));

        // Запрашиваем отчёт по явным user_ids (включая уволенных при showFired),
        // а не через all_users/group_id — бэкенд там отдаёт только активных.
        const reportData = await getAccountingReportHours(
          null,
          reportUserIds,
          dateFrom,
          dateTo,
          false
        );

        console.log("Raw API data:", reportData);

        if (!reportData || reportData.length === 0) {
          setReportsData([]);
          setNoDataMessage("Нет данных для формирования отчета");
          return;
        }

        const formattedData = reportData
          .map((report) => {
            if (!report || !report.id) return null;

            return {
              userId: `${report.id}-${report.id}`,
              userName: report.user || "Не указано",
              isFired: firedUserIds.has(Number(report.id)),
              workingHoursPerMonth: safeNumber(report.month_work_hours, 0),
              salary: safeNumber(report.salary, 0),
              workedHours: safeNumber(report.fact_work_hours, 0),
              normalHours: safeNumber(
                report.fact_work_hours_without_overtime,
                0
              ),
              overtimeHours: safeNumber(report.only_overtime, 0),
              normalSalary: safeNumber(report.wage_without_overtime, 0),
              overtimeSalary: safeNumber(report.wage_only_overtime, 0),
              totalSalary: safeNumber(report.wage, 0),
            };
          })
          .filter(Boolean);

        if (formattedData.length === 0) {
          setNoDataMessage("Нет данных для формирования отчета");
        } else {
          setNoDataMessage("");
        }

        setReportsData(formattedData);
      } catch (error) {
        console.error("Ошибка при загрузке отчета:", error);
        setReportsData([]);
        setNoDataMessage("Ошибка при загрузке данных");
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [
    selectedUsers,
    dateRange,
    selectedDepartment,
    selectionMode,
    allUsers,
    users,
    firedUserIds,
    showFired,
  ]);

  // Упрощаем функцию getFilteredReportsData
  const getFilteredReportsData = () => {
    if (!reportsData.length) return [];

    return reportsData
      .map((report) => {
        if (!report) return null;
        if (!showFired && report.isFired) return null;

        return {
          ...report,
          workedHours: safeNumber(report.normalHours, 0),
          calculatedSalary: safeNumber(report.normalSalary, 0),
        };
      })
      .filter(Boolean);
  };

  // Добавим функцию для подсчета итогов
  const calculateTotals = (data) => {
    return data.reduce(
      (acc, report) => {
        return {
          totalWorkedHours:
            acc.totalWorkedHours + safeNumber(report.workedHours, 0),
          totalSalary: acc.totalSalary + safeNumber(report.calculatedSalary, 0),
        };
      },
      { totalWorkedHours: 0, totalSalary: 0 }
    );
  };

  const handleRemoveUser = (userId) => {
    // Извлекаем оригинальный ID пользователя из составного ключа
    const originalUserId = userId.split("-")[0];

    // Удаляем пользователя из выбранных
    const updatedUsers = selectedUsers.filter(
      (user) => String(user.id) !== originalUserId
    );
    setSelectedUsers(updatedUsers);

    // Если это был последний пользователь в таблице
    if (updatedUsers.length === 0) {
      setSelectionMode("individual");
      setSelectedDepartment(null);
    } else if (selectionMode === "department") {
      setSelectionMode("individual");
      setSelectedDepartment(null);
    }

    // Обновляем отчеты только для оставшихся пользователей
    const updatedReportsData = reportsData.filter(
      (report) => report.userId !== userId
    );
    setReportsData(updatedReportsData);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    setExportError("");
    try {
      const dateFrom = getFormattedDate(getMonthStart(dateRange.startDate));
      const dateTo = getFormattedDate(getMonthEnd(dateRange.startDate));

      const userIds = getReportUserIds();
      if (!userIds.length) {
        throw new Error("Нет сотрудников для выгрузки отчёта");
      }
      await getAccountingReportExcel(null, userIds, dateFrom, dateTo, null, false);
    } catch (error) {
      console.error("Ошибка при выгрузке отчета:", error);
      setExportError(error.message || "Произошла ошибка при выгрузке отчета");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportToExcelV3 = async () => {
    setIsExportingV3(true);
    setExportError("");
    try {
      const dateFrom = getFormattedDate(getMonthStart(dateRange.startDate));
      const dateTo = getFormattedDate(getMonthEnd(dateRange.startDate));

      const userIds = getReportUserIds();
      if (!userIds.length) {
        throw new Error("Нет сотрудников для выгрузки отчёта");
      }
      await getAccountingReportExcelV3(null, userIds, dateFrom, dateTo, false);
    } catch (error) {
      console.error("Ошибка при выгрузке отчета (с историей зарплат):", error);
      setExportError(
        error.message || "Произошла ошибка при выгрузке отчета"
      );
    } finally {
      setIsExportingV3(false);
    }
  };

  // Добавим новые функции для работы с датами
  const getMonthStart = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    d.setHours(12, 0, 0, 0);
    return d;
  };

  const getMonthEnd = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    d.setHours(12, 0, 0, 0);
    return d;
  };

  // Модифицируем обработчик изменения даты
  const handleStartDateChange = (date) => {
    if (!date) return;

    const monthStart = getMonthStart(date);
    const monthEnd = getMonthEnd(date);

    setDateRange({
      startDate: monthStart,
      endDate: monthEnd,
    });
  };

  return (
    <section className="financial-report">
      <h2 className="financial-report__title">Отчет по сотрудникам:</h2>
      {isLoadingUsers ? (
        <div className="financial-report__loader">
          <Preloader />
        </div>
      ) : (
        <div
          className="selectors-container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            marginBottom: "20px",
          }}
        >
          <div
            className="mode-selector"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <label className="filter-control">
              <input
                type="radio"
                name="selectionMode"
                value="department"
                checked={selectionMode === "department" && !allUsers}
                onChange={(e) => {
                  setSelectionMode(e.target.value);
                  setSelectedUsers([]);
                  setAllUsers(false);
                }}
              />
              <span>По отделу</span>
            </label>
            <label className="filter-control">
              <input
                type="radio"
                name="selectionMode"
                value="individual"
                checked={selectionMode === "individual" && !allUsers}
                onChange={(e) => {
                  setSelectionMode(e.target.value);
                  setSelectedUsers([]);
                  setSelectedDepartment(null);
                  setAllUsers(false);
                }}
              />
              <span>Индивидуально</span>
            </label>
            <label className="filter-control">
              <input
                type="radio"
                name="selectionMode"
                value="all"
                checked={allUsers}
                onChange={(e) => {
                  setAllUsers(true);
                  setSelectionMode("all");
                  setSelectedUsers([]);
                  setSelectedDepartment(null);
                }}
              />
              <span>Все сотрудники</span>
            </label>
            <label className="filter-control">
              <input
                type="checkbox"
                checked={showFired}
                onChange={(e) => setShowFired(e.target.checked)}
              />
              <span>Показывать уволенных</span>
            </label>
          </div>

          {selectionMode === "department" && !allUsers ? (
            <div
              className="selector"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                maxWidth: "400px",
              }}
            >
              <label style={{ marginBottom: "10px" }}>Отдел:</label>
              <Select
                value={selectedDepartment}
                onChange={(option) => setSelectedDepartment(option)}
                options={departments}
                placeholder="Выберите отдел"
                isClearable
                styles={{
                  ...customStyles,
                  control: (base) => ({
                    ...base,
                    width: "400px",
                    borderRadius: "10px",
                    marginBottom: "20px",
                  }),
                  menu: (base) => ({
                    ...base,
                    width: "400px",
                    borderRadius: "10px",
                  }),
                }}
                className="department-select"
              />
            </div>
          ) : selectionMode === "individual" && !allUsers ? (
            <div
              className="selector"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                maxWidth: "400px",
              }}
            >
              <label style={{ marginBottom: "10px" }}>Сотрудники:</label>
              <Select
                isMulti
                value={getVisibleUsers(selectedUsers).map((user) => ({
                  value: user.id,
                  label: getUserOptionLabel(user),
                }))}
                onChange={(options) => {
                  const nextSelectedUsers = (options || []).map((option) =>
                    users.find((u) => u.id === option.value)
                  ).filter(Boolean);
                  setSelectedUsers(nextSelectedUsers);
                }}
                options={getVisibleUsers(users).map((user) => ({
                  value: user.id,
                  label: getUserOptionLabel(user),
                }))}
                placeholder="Выберите сотрудников"
                styles={{
                  ...customStyles,
                  control: (base) => ({
                    ...base,
                    width: "400px",
                    borderRadius: "10px",
                    marginBottom: "20px",
                  }),
                  menu: (base) => ({
                    ...base,
                    width: "400px",
                    borderRadius: "10px",
                  }),
                  multiValue: (base) => ({
                    ...base,
                    borderRadius: "5px",
                  }),
                }}
                className="employee-select"
              />
            </div>
          ) : null}
        </div>
      )}

      {(selectedUsers.length > 0 ||
        allUsers ||
        (selectionMode === "department" && selectedDepartment)) &&
        !isLoadingUsers && (
          <>
            <div
              className="timesheet-controls"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              <div
                className="date-range-picker"
                style={{ marginRight: "20px" }}
              >
                <label style={{ marginRight: "10px" }}>Месяц:</label>
                <DatePicker
                  selected={dateRange.startDate}
                  onChange={handleStartDateChange}
                  dateFormat="MM.yyyy"
                  showMonthYearPicker
                  locale="ru"
                  className="timesheet-datepicker"
                  calendarClassName="custom-calendar month-year-calendar"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    className="department-timesheet__export-button"
                    onClick={handleExportToExcel}
                    disabled={
                      (!selectedUsers.length &&
                        !selectedDepartment &&
                        !allUsers) ||
                      isExporting ||
                      isExportingV3
                    }
                  >
                    {isExporting ? (
                      <>
                        <span className="button-loader"></span>
                        Загрузка...
                      </>
                    ) : (
                      "Выгрузить в Excel"
                    )}
                  </button>
                  <button
                    className="department-timesheet__export-button"
                    onClick={handleExportToExcelV3}
                    disabled={
                      (!selectedUsers.length &&
                        !selectedDepartment &&
                        !allUsers) ||
                      isExporting ||
                      isExportingV3
                    }
                  >
                    {isExportingV3 ? (
                      <>
                        <span className="button-loader"></span>
                        Загрузка...
                      </>
                    ) : (
                      "Выгрузить в Excel (с историей зарплат)"
                    )}
                  </button>
                </div>
                {exportError && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: "14px",
                      marginTop: "10px",
                      textAlign: "center",
                      maxWidth: "400px",
                      padding: "10px",
                      backgroundColor: "#ffebee",
                      borderRadius: "4px",
                      border: "1px solid #ffcdd2",
                    }}
                  >
                    {exportError}
                  </div>
                )}
              </div>
            </div>
            {loading ? (
              <div className="financial-report__loader">
                <Preloader />
              </div>
            ) : noDataMessage ? (
              <div className="financial-report__no-data">{noDataMessage}</div>
            ) : (
              getFilteredReportsData().length > 0 && (
                <div className="timesheet-table-container">
                  <table className="timesheet-table">
                    <thead>
                      <tr>
                        <th>ФИО</th>
                        <th>Рабочих часов</th>
                        <th>Оклад</th>
                        <th>Отработано часов</th>
                        <th>Заработная плата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredReportsData().map((report) => (
                        <tr
                          key={report.userId}
                          className={[
                            report.overtimeHours > 0 ? "overtime-row" : "",
                            report.isFired ? "fired-row" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <td>
                            <div className="user-row-container">
                              <button
                                className="remove-user-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveUser(report.userId);
                                }}
                                title="Удалить из таблицы"
                              >
                                ×
                              </button>
                              <span className="user-row-name">
                                {report.userName || "Н/Д"}
                              </span>
                              {report.isFired && (
                                <span className="fired-badge">уволен</span>
                              )}
                            </div>
                          </td>
                          <td>{report.workingHoursPerMonth || 0}</td>
                          <td>
                            {(report.salary || 0).toLocaleString("ru-RU")} тг.
                          </td>
                          <td>{report.workedHours || 0}</td>
                          <td>
                            {(report.calculatedSalary || 0).toLocaleString(
                              "ru-RU"
                            )}{" "}
                            тг.
                          </td>
                        </tr>
                      ))}
                      {/* Добавляем итоговую строку */}
                      {getFilteredReportsData().length > 0 && (
                        <tr className="total-row">
                          <td colSpan="3">
                            <strong>Итого:</strong>
                          </td>
                          <td>
                            <strong>
                              {
                                calculateTotals(getFilteredReportsData())
                                  .totalWorkedHours
                              }
                            </strong>
                          </td>
                          <td>
                            <strong>
                              {calculateTotals(
                                getFilteredReportsData()
                              ).totalSalary.toLocaleString("ru-RU")}{" "}
                              тг.
                            </strong>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </>
        )}
    </section>
  );
}

export default FinancialReport;
